import {
  Box,
  Center,
  Flex,
  Grid,
  Image,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { Play } from 'lucide-react';
import { albumItems, albumLayout } from '../../lib/albums';
import { mediaSecrets, useMediaUrl } from '../../lib/e2ee/media';
import {
  formatTime,
  type ChatMessage,
  type MessageMetadata,
} from './messageUtils';
import { useT } from '../../i18n';

// An album's collage (M60, D219): two side by side, three as one big and two
// small, four or more as a 2×2 grid with "+N" on the last tile. Each tile is
// the item's small preview, decrypted on the device; tapping one opens the
// viewer there.

const WIDTH = 300;

function Tile({
  item,
  index,
  total,
  extra,
  onOpen,
  onLoad,
}: {
  item: ChatMessage;
  index: number;
  total: number;
  extra: number;
  onOpen: (index: number) => void;
  onLoad: () => void;
}) {
  const t = useT();
  const meta = (item.metadata ?? {}) as MessageMetadata;
  const secrets = mediaSecrets(item);
  // Videos always have a poster; photos may be small enough to have none.
  const hasThumb = Boolean(meta.thumbnailUrl);
  const preview = useMediaUrl(
    hasThumb
      ? meta.thumbnailUrl
      : item.type === 'video'
        ? undefined
        : item.content,
    hasThumb ? secrets.thumb : secrets.file,
    hasThumb ? 'image/jpeg' : secrets.mimeType
  );
  return (
    <Box
      as="button"
      type="button"
      position="relative"
      overflow="hidden"
      bg="surface.subtle"
      w="100%"
      h="100%"
      minH={0}
      aria-label={t('chat.album.open', { n: index + 1, total })}
      onClick={() => onOpen(index)}
    >
      {preview.src ? (
        <Image
          src={preview.src}
          alt=""
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          objectFit="cover"
          onLoad={onLoad}
        />
      ) : (
        <Center position="absolute" inset={0}>
          {!preview.failed && <Spinner size="sm" color="accent" />}
        </Center>
      )}
      {item.type === 'video' && (
        <Center position="absolute" inset={0}>
          <Flex
            w="40px"
            h="40px"
            borderRadius="full"
            bg="blackAlpha.600"
            color="white"
            align="center"
            justify="center"
          >
            <Play size={18} fill="currentColor" style={{ marginLeft: 2 }} />
          </Flex>
          {meta.duration ? (
            <Text
              position="absolute"
              left="8px"
              bottom="6px"
              fontSize="12px"
              fontWeight={600}
              color="white"
              bg="blackAlpha.600"
              px={2}
              borderRadius="full"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatTime(meta.duration)}
            </Text>
          ) : null}
        </Center>
      )}
      {extra > 0 && (
        <Center
          position="absolute"
          inset={0}
          // A fixed 45% black, whatever the photo or theme, so "+N" reads.
          bg="rgba(0, 0, 0, 0.45)"
          textShadow="0 1px 3px rgba(0, 0, 0, 0.6)"
          color="white"
          fontSize="26px"
          fontWeight={700}
        >
          +{extra}
        </Center>
      )}
    </Box>
  );
}

export function AlbumGrid({
  message,
  onOpen,
  onLoad,
}: {
  message: ChatMessage;
  onOpen: (index: number) => void;
  onLoad: () => void;
}) {
  const t = useT();
  const items = albumItems(message);
  const layout = albumLayout(items.length);
  const shown = items.slice(0, layout.shown);
  const tile = (item: ChatMessage, index: number) => (
    <Tile
      key={index}
      item={item}
      index={index}
      total={items.length}
      extra={index === layout.shown - 1 ? layout.extra : 0}
      onOpen={onOpen}
      onLoad={onLoad}
    />
  );

  return (
    <Grid
      role="group"
      aria-label={t('chat.album.label', { n: items.length })}
      data-testid="album-grid"
      data-layout={layout.kind}
      w={`${WIDTH}px`}
      maxW="100%"
      gap="3px"
      borderRadius="20px"
      overflow="hidden"
      templateColumns="repeat(2, minmax(0, 1fr))"
      templateRows={layout.kind === 'two' ? '1fr' : 'repeat(2, minmax(0, 1fr))'}
      sx={{ aspectRatio: layout.kind === 'two' ? '2 / 1.2' : '1 / 1' }}
    >
      {layout.kind === 'three' ? (
        <>
          <Box gridRow="span 2">{tile(shown[0]!, 0)}</Box>
          {tile(shown[1]!, 1)}
          {tile(shown[2]!, 2)}
        </>
      ) : (
        shown.map(tile)
      )}
    </Grid>
  );
}
